import React, { useEffect, useRef } from 'react';
import { Avatar, Tooltip } from '@mui/material';
import DialogBase from './DialogBase';
import { contentService } from '../../../services/content.service';
import { helperService } from '../../../services/helper.service';

// Types
export type TimelineType = 'beoordeling' | 'task' | 'annotation';

interface TimelineItem {
  key: { id: string };
  timestamp: string;
  user: string;
  remark?: string;
  title?: string | null;
  description?: string | null;
  createdByUser?: string | null;
  createdByOrganization?: string | null;
  status?: string;
  assignedOrganization?: string | null;
  dueDate?: string | null;
  // [key: string]: any;
}

interface DialogTimelineProps {
  open: boolean;
  items: TimelineItem[];
  type: TimelineType;
  title?: string;
  organizations?: { id: string; name: string }[];
  onClose: () => void;
}

// Constants
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

// Component
const DialogTimeline: React.FC<DialogTimelineProps> = ({
  open,
  items,
  type,
  title = 'Historie',
  organizations,
  onClose,
}) => {
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
    }
  }, []);

  const sortedItems = [...items].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const getIcon = (item: TimelineItem) => {
    switch (type) {
      case 'beoordeling':
        return contentService.getBeoordelingStatusIcon(item.key.id);
      case 'task':
        return contentService.getTaskStatusIcon(item.status);
      case 'annotation': // Geen annotation status
      default:
        return 'history';
    }
  };

  const getLabel = (item: TimelineItem) => {
    switch (type) {
      case 'beoordeling':
        return contentService.getBeoordelingStatusLabel(item.key.id);
      case 'task':
        return contentService.getTaskStatusLabel(item.status);
      case 'annotation': // Geen annotation status
      default:
        return 'Geen status label beschikbaar';
    }
  };

  const getOrganisatieNaam = (organisatieId: string) => {
    const organisatie = organizations?.find(org => org.id === organisatieId);
    return organisatie?.name ?? organisatieId; // fallback to ID if name not found
  };

  const renderItemContent = (item: TimelineItem) => {
    switch (type) {
      case 'beoordeling': {
        const label = getLabel(item);
        return (
          <>
            <div className="status">
              <strong>Status:</strong> {label}
            </div>

            <div className="timeline-data">
              {item.remark && <p>{item.remark}</p>}
            </div>
  
            <div className="meta">
              {new Date(item.timestamp).toLocaleString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }).replace(':', '.') + ' uur'
              } - {item.user.toLowerCase()}
            </div>
          </>
        );
      }
  
      case 'task': {
        const label = getLabel(item);
        return (
          <>
            <div className="timeline-status">
              <strong>Status:</strong> {label}
            </div>
  
            <div className="timeline-data">
              {item.title && <h4 className="timeline-title">{item.title}</h4>}
              <p>
                {item.description && (
                  <>
                    {item.description}
                    <br />
                  </>
                )}

                {item.assignedOrganization && (
                  <>
                    <b>Verantwoordelijk:</b> {getOrganisatieNaam(item.assignedOrganization)}
                  </>
                )}
              </p>
            </div>

            <div className="timeline-meta">
              {new Date(item.timestamp).toLocaleString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }).replace(':', '.') + ' uur'
              } - {item.createdByUser?.toLowerCase() || 'onbekend'}
              {item.createdByOrganization && ` (${getOrganisatieNaam(item.createdByOrganization)})`}
              {item.dueDate && <> - Deadline: {new Date(item.dueDate).toLocaleDateString('nl-NL')}</>}
            </div>
          </>
        );
      }
  
      case 'annotation': {
        return (
          <>
            {item.title && (
              <div>
                <strong>Titel:</strong> {item.title}
              </div>
            )}
  
            <div className="timeline-data">
              {item.description && <p>{item.description}</p>}
            </div>

            <div className="timeline-meta">
              {new Date(item.timestamp).toLocaleString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }).replace(':', '.') + ' uur'
              } - {item.createdByUser?.toLowerCase() || 'onbekend'}
              {item.createdByOrganization && ` (${getOrganisatieNaam(item.createdByOrganization)})`}
            </div>
          </>
        );
      }
  
      default:
        return (
          <>
            <div className="timeline-status">
              <strong>Status:</strong> {getLabel(item)}
            </div>
            <div className="timeline-meta">
              {new Date(item.timestamp).toLocaleDateString('nl-NL')} - {item.user?.toLowerCase()}
            </div>
          </>
        );
    }
  };

  return (
    <DialogBase
      open={open}
      title={title}
      onClose={onClose}
      width="800px"
    >
      <section>
        {sortedItems.length > 0 ? (
          <div className="timeline-container">
            {sortedItems.map((item, index) => (
              <div className="timeline-item" key={index}>
                <div className="timeline-marker">
                  {type === 'annotation' ? (
                    <Tooltip title={item.createdByUser || 'onbekend'} placement="right" arrow>
                      <Avatar
                        className="timeline-avatar"
                        sx={{ bgcolor: helperService.getUserColor(item.createdByUser || '?') }}
                      >
                         {(item.createdByUser || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  ) : (
                    <Tooltip title={getLabel(item)} placement="right" arrow>
                      <span className={`timeline-icon ${item.key?.id || item.status}`}>
                        <span className="material-icons">
                          {getIcon(item)}
                        </span>
                      </span>
                    </Tooltip>
                  )}
                </div>


                <div className="timeline-content">
                  {renderItemContent(item)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>Er is geen geschiedenis beschikbaar</p>
        )}
      </section>
    </DialogBase>
  );
};

export default DialogTimeline;
